// docs/25 D9, docs/84 — the post-encounter Spoils, drawn over the encounter itself (the board stays
// up underneath, the PARTY cards stay on the left, the encounter's chat stays where it was): a
// panel over the board with the outcome, the haul dealt into one pile per hero (D6), each pile
// carrying the XP every hero takes whole (D5) as its last line; click a pile to claim it for the
// hero you are choosing for, click it again to let it go; click an item to read what it does. The
// bottom bar holds the timer, who has locked in, Lock in / Unlock, "View the field" (the panel
// folds away so the map can be looked over while others choose) and Continue, which hands back
// to the RouteDriver (LocalMap, epilogue, Aftermath or the lobby). Layout classes `fk-spoils__*`
// live in spoils.css.
import { gradeTier, gradeWord } from '../../core/sim/gear';
import { GearBody, ItemBody, ItemIcon, itemTooltip } from '../inventory/ItemCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, EmptyState, HeroSprite, Meter, OryxIcon, Portrait, Reveal, Segmented, Stamp, type StampTone } from '../components';
import { useRoute } from '../../core/stores/route';
import { useSession } from '../../core/stores/session';
import { useSpoils } from '../../core/stores/spoils';
import { useOverlay } from '../overlay';
import { useControlGrants, useDividedItems, useEncounterResults, useEncounterRow, useHeroCallings, useMyLevelUpOffers, useNames, useRewardDivision, useRewardLocks, useRewardPiles, useSpoilsSkims, useWorldHeroes } from '../../core/subscriptions/hooks';
import { call } from '../../core/net/reducers';
import { identityHex, requireConn, tsToMs } from '../../core/net/connection';
import { formatMmSs, secondsUntil, serverNow } from '../../core/time';
import { CLASS_NAMES } from '../../core/sim/adapt';
import { catalogueItem } from '../../core/items/catalogue';
import { OUTCOMES } from './levelupData';
import { beastOf } from './Lobby';
import { playSfx, type SfxKey } from '../../core/audio';
import { usePlace } from '../../core/audio/places';
import { rewardSfx } from '../sfx';
import { useEncounter } from '../../core/encounter/session';

/** The choosing window (fk-world `spoils::CHOOSE_S`). */
export const CHOOSE_S = 120;
const DIV_GIVEN = 1;
/** Stamp tone per encounter outcome (OUTCOMES: Victory, Wipe, Fled, Surrendered, Stalemate, Abandoned). */
const OUTCOME_TONE: StampTone[] = ['victory', 'defeat', 'neutral', 'victory', 'neutral', 'neutral'];
const STASH_NAMES = ['a chest', 'a hidden cache', 'the war chest', "a fallen foe's pouch", 'a find'];
/** docs/138 XO4: an exploration's spoils are personal — the division comes given, one pile per hero. */
export const KEEPERS_LINE = 'Finders keepers: each hero keeps what they found. Experience is shared.';

const spoilsRpc = {
  claimPile: (encounterId: bigint, heroId: bigint, index: number) => call('claim_pile', () => requireConn().reducers.claimPile({ encounterId, heroId, index })),
  releasePile: (encounterId: bigint, heroId: bigint) => call('release_pile', () => requireConn().reducers.releasePile({ encounterId, heroId })),
  lockRewards: (encounterId: bigint) => call('lock_rewards', () => requireConn().reducers.lockRewards({ encounterId })),
  unlockRewards: (encounterId: bigint) => call('unlock_rewards', () => requireConn().reducers.unlockRewards({ encounterId })),
};

const tierTone = (tier: number) => (tier >= 3 ? 'gold' : tier === 2 ? 'green' : 'grey');

const SUPPLIES = /potion|draught|elixir|tonic|philter|poultice|salve|ration|bread|cheese|ale|wine|flask|vial|herb/i;
/** docs/44: what claiming a pile sounds like, by its best thing: a relic or masterwork glints, a draught or
 *  supplies clink, a pile of only gold is a heavy purse, anything else is an item collected. */
export function claimSfx(items: ReadonlyArray<{ tier: number; relic: boolean; name: string }>, gold: number): SfxKey {
  if (items.some((i) => i.relic || i.tier >= 3)) return 'ui.gems';
  if (items.length === 0) return gold > 0 ? 'ui.coins_big' : 'ui.click';
  if (items.every((i) => SUPPLIES.test(i.name))) return 'ui.potion';
  return 'ui.item';
}

/** The post-encounter phrase under the stamp, by outcome and division. */
export function haulLine(d: { lost: boolean; piles: number; state: number } | undefined, items: number, gold: number): string {
  if (!d) return 'Counting the haul…';
  if (d.lost) return items + gold > 0 ? 'The party fell, and what it had taken stays on the field.' : 'The party fell; nothing was carried out.';
  if (d.piles === 0) return 'Nothing was taken: no stash was opened.';
  return d.state === DIV_GIVEN ? 'The spoils are given.' : `${items} item${items === 1 ? '' : 's'} and ${gold} gold, dealt into ${d.piles} even pile${d.piles === 1 ? '' : 's'}.`;
}

/** docs/84 SP3: what a click on a pile does for the hero being chosen for. A pile one of my heroes
 *  holds is let go (whichever of mine holds it); a free pile is claimed; anything else does nothing. */
export function pileClick(p: { holder?: bigint; given: boolean; locked: boolean; choosing?: bigint; mine: readonly bigint[] }): { kind: 'claim'; hero: bigint } | { kind: 'release'; hero: bigint } | undefined {
  if (p.given || p.locked) return undefined;
  if (p.holder !== undefined) return p.mine.includes(p.holder) ? { kind: 'release', hero: p.holder } : undefined;
  return p.choosing !== undefined ? { kind: 'claim', hero: p.choosing } : undefined;
}

/** Everything the panel and the bar both read, from the encounter's division rows. */
function useSpoilsModel() {
  const encounterId = useRoute((s) => s.encounterId);
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const enc = useEncounterRow(encounterId);
  const division = useRewardDivision(encounterId);
  const piles = useRewardPiles(encounterId);
  const locks = useRewardLocks(encounterId);
  const items = useDividedItems(encounterId);
  const results = useEncounterResults(encounterId);
  const heroes = useWorldHeroes(worldId);
  const grants = useControlGrants(worldId);
  const names = useNames(worldId);
  const callings = useHeroCallings(worldId);
  // the chooser is shared between the panel and the bar
  const forHero = useSpoils((s) => s.forHero);
  const setForHero = useSpoils((s) => s.setForHero);

  const heroById = useMemo(() => new Map(heroes.map((h) => [h.id.toString(), h])), [heroes]);
  const controllerOf = (heroId: bigint): string => {
    const g = grants.find((x) => x.heroId === heroId);
    if (g) return identityHex(g.grantee);
    const h = heroById.get(heroId.toString());
    return h ? identityHex(h.owner) : '';
  };
  const party = [...results].sort((a, b) => (a.heroId < b.heroId ? -1 : 1));
  const mine = party.filter((r) => controllerOf(r.heroId) === me).map((r) => r.heroId);
  const owners = [...new Set(party.map((r) => controllerOf(r.heroId)).filter(Boolean))];
  const lockedBy = new Set(locks.map((l) => identityHex(l.owner)));
  const sortedPiles = [...piles].sort((a, b) => a.index - b.index);
  const heldBy = (heroId: bigint) => sortedPiles.find((p) => p.heroId === heroId);
  // the hero I am choosing for: the one I picked, else the first of mine still without a pile, else the first of mine
  const choosing = mine.find((h) => h.toString() === forHero) ?? mine.find((h) => !heldBy(h)) ?? mine[0];
  // docs/138 XO4: the board under the Spoils is still the exploration's
  const explore = useEncounter((s) => !!s.snapshot?.explore);
  const outcome = enc?.outcome ?? division?.outcome;
  const known = outcome !== undefined && outcome !== null;
  return {
    encounterId, worldId, me, enc, division, explore, items, party, mine, owners, lockedBy, sortedPiles, heldBy, choosing, setForHero, names, callings, heroById,
    iLocked: lockedBy.has(me),
    given: division?.state === DIV_GIVEN,
    word: known ? OUTCOMES[outcome] ?? 'Ended' : 'Ended',
    tone: known ? OUTCOME_TONE[outcome] ?? 'neutral' : 'neutral',
    xpEach: division?.xpEach ?? party[0]?.xp ?? 0,
    haulGold: sortedPiles.reduce((s, p) => s + p.gold, 0),
  };
}

/** A hero's face, and a ranger's beast at their heel (docs/59 companions). */
function HeroFace({ hero, beast, scale = 2 }: { hero?: { id: bigint; species: number; name: string; oryxFrame: number; class: number }; beast?: { name: string; kind: string; frame: number }; scale?: 2 | 3 }) {
  return (
    <span className="fk-spoils__face" aria-hidden="true">
      <Portrait heroId={hero?.id} species={hero?.species} frame={hero?.oryxFrame} classId={CLASS_NAMES[hero?.class ?? 0]} scale={scale} title={hero?.name} />
      {beast && <span className="fk-spoils__beast" data-testid="spoils-beast" data-beast-name={beast.name}><HeroSprite frame={beast.frame} classId="companion" scale={2} title={beast.name === beast.kind ? beast.kind : `${beast.name} the ${beast.kind}`} /></span>}
    </span>
  );
}

/**
 * The Spoils over the board (the encounter HUD's stage while the route is `spoils`). `open` false
 * folds it away so the field can be looked over; the bar's "Show the spoils" brings it back.
 */
export function SpoilsPanel({ open }: { open: boolean }) {
  const m = useSpoilsModel();
  const { encounterId, enc, division, items, mine, sortedPiles, choosing, given, iLocked, heroById, callings } = m;
  const skims = useSpoilsSkims(encounterId);
  const offers = useMyLevelUpOffers().filter((o) => o.worldId === m.worldId);
  const openLevelUp = useOverlay((s) => s.openLevelUp);
  const [inspect, setInspect] = useState<bigint>();
  // docs/34: the spoils open with a chest (a finer one when a masterwork lies in them), once per fight
  const opened = useRef<bigint | undefined>(undefined);
  useEffect(() => {
    if (encounterId === undefined || opened.current === encounterId || sortedPiles.length === 0) return;
    opened.current = encounterId;
    playSfx('ui.chest');
    playSfx(rewardSfx(items.some((i) => i.tier >= 3) ? 'ui.loot_rare' : 'ui.loot'), { delayMs: 350 });
    // docs/44: the experience tallies up under the chest, a rising run of points
    for (let n = 0; n < 5; n++) playSfx('ui.points', { delayMs: 1300 + n * 140, rate: 1 + n * 0.06, gain: 0.7 });
  }, [encounterId, sortedPiles.length, items]);
  // docs/44: the camp after the fight, its fire under it
  usePlace({ key: 'place.camp', layers: ['place.fire'] });

  const beast = (heroId: bigint) => beastOf(callings, heroId);
  const shown = items.find((i) => i.id === inspect);
  const shownDef = shown ? catalogueItem(shown.def) : undefined;
  const choosingHero = choosing !== undefined ? heroById.get(choosing.toString()) : undefined;
  const choosingNow = !!division && division.piles > 0 && !given;

  return (
    <section className={`fk-spoils fk-hit ${open ? '' : 'fk-spoils--folded'}`} aria-label="The spoils" aria-hidden={!open} data-testid="spoils-panel">
      <header className="fk-spoils__head">
        <Stamp small tone={m.tone} sub={enc ? `${enc.name} · ${enc.pulseIndex} turn${enc.pulseIndex === 1 ? '' : 's'}` : undefined} data-testid="spoils-stamp">{m.word}</Stamp>
        <div className="fk-spoils__summary">
          <span className="fk-spoils__xp-each" data-testid="spoils-xp">+{m.xpEach} XP to every hero</span>
          <p className="fk-spoils__line" data-testid="spoils-haul-line">{m.explore && division && !division.lost ? KEEPERS_LINE : haulLine(division, items.length, m.haulGold)}</p>
          {/* docs/59 CC3: a thieving rogue's cut came off the top before the piles were dealt */}
          {skims.map((k) => (
            <p key={k.id.toString()} className="fk-spoils__line fk-spoils__line--skim" data-testid="spoils-skim" data-gold={k.gold}>
              <OryxIcon name="item_coin_bag" scale={1} /> {heroById.get(k.heroId.toString())?.name ?? 'A rogue'} skimmed {k.gold} gold off the top before the split.
            </p>
          ))}
        </div>
        {offers.length > 0 && (
          <div className="fk-spoils__levelups">
            {offers.map((o) => (
              <Button key={o.id.toString()} small primary onClick={() => openLevelUp(o.id)} data-testid="spoils-levelup">
                ★ {heroById.get(o.heroId.toString())?.name ?? 'A hero'} reaches level {o.toLevel}
              </Button>
            ))}
          </div>
        )}
      </header>

      {choosingNow && mine.length > 0 && choosing !== undefined && (
        <p className="fk-spoils__prompt" data-testid="spoils-prompt">
          {iLocked ? 'You have locked in. Unlock to switch.' : <>Pick a pile for <b>{choosingHero?.name ?? 'your hero'}</b>. Click your pile again to put it back.</>}
        </p>
      )}

      {division && division.piles === 0 && (
        <EmptyState glyph={division.lost ? '✝' : '✦'} title={division.lost ? 'Left on the field' : 'Empty-handed'} data-testid={division.lost ? 'spoils-lost' : 'spoils-empty'}>
          {division.lost ? 'A fallen party carries nothing home.' : m.explore ? 'Nobody picked anything up. Next time, Search as you go: hidden finds lie all over an exploration.' : 'Next time, open the chests — and look in the corners.'}
        </EmptyState>
      )}

      {sortedPiles.length > 0 && (
        <div className="fk-spoils__body">
          <div className="fk-spoils__piles">
            {sortedPiles.map((p, k) => {
              const pileItems = items.filter((i) => i.pile === p.index).sort((a, b) => b.value - a.value);
              const holder = p.heroId ?? undefined;
              const h = holder !== undefined ? heroById.get(holder.toString()) : undefined;
              const holderMine = holder !== undefined && mine.includes(holder);
              const free = holder === undefined;
              const act = pileClick({ holder, given, locked: iLocked, choosing, mine });
              const click = () => {
                if (!act) return;
                if (act.kind === 'claim') void spoilsRpc.claimPile(p.encounterId, act.hero, p.index);
                else void spoilsRpc.releasePile(p.encounterId, act.hero);
              };
              return (
                <div key={p.id.toString()} role={act ? 'button' : undefined} tabIndex={act ? 0 : undefined} onClick={click}
                  onKeyDown={(e) => { if (act && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); click(); } }}
                  data-sfx={act?.kind === 'claim' ? claimSfx(pileItems, p.gold) : act ? 'ui.click' : 'none'}
                  aria-label={act ? (act.kind === 'claim' ? `Claim pile ${p.index + 1}` : `Put pile ${p.index + 1} back`) : `Pile ${p.index + 1}`}
                  className={`fk-spoils__pile fk-card--rise ${act ? 'fk-spoils__pile--act' : ''} ${holderMine ? 'fk-spoils__pile--mine' : ''} ${!free && !holderMine && !given ? 'fk-spoils__pile--theirs' : ''}`}
                  style={{ ['--fk-i' as string]: k }} data-testid="pile-card" data-index={p.index} data-holder={holder?.toString() ?? ''}>
                  <div className="fk-spoils__pile-head">
                    <OryxIcon name={free ? 'chest_gold_closed' : 'chest_gold_open'} scale={2} title={`Pile ${p.index + 1}`} />
                    {/* docs/138 XO4: in an exploration each pile is one hero's own finds */}
                    <span className="fk-spoils__pile-name">{m.explore && h ? `${h.name}'s finds` : `Pile ${p.index + 1}`}</span>
                    {!m.explore && <span className="fk-spoils__value" title="What fairness is measured in: item value plus gold">worth {p.value}</span>}
                  </div>
                  <ul className="fk-spoils__items">
                    {pileItems.map((i) => (
                      <li key={i.id.toString()}>
                        <button type="button" className={`fk-spoils__item ${inspect === i.id ? 'fk-spoils__item--shown' : ''}`} data-testid="pile-item" data-sfx="ui.click" title={itemTooltip(i.def, i.tier, i.name, i.relic)}
                          aria-pressed={inspect === i.id} onClick={(e) => { e.stopPropagation(); setInspect(inspect === i.id ? undefined : i.id); }}>
                          <ItemIcon def={i.def} tier={i.tier} name={i.name} relic={i.relic} scale={1} framed />
                          <span className="fk-spoils__item-name">{i.name}</span>
                          {i.relic ? <Badge tone="plum">relic</Badge> : <Badge tone={tierTone(gradeTier(i.def, i.tier))}>{gradeWord(i.def, i.tier)}</Badge>}
                        </button>
                      </li>
                    ))}
                    {p.gold > 0 && <li className="fk-spoils__line-item"><OryxIcon name="item_coin_bag" scale={1} /><span className="fk-spoils__item-name">{p.gold} gold</span></li>}
                    {/* docs/84 SP2: the XP is every pile's last line (every hero takes it whole, whichever pile they hold) */}
                    <li className="fk-spoils__line-item fk-spoils__line-item--xp" data-testid="pile-xp"><span className="fk-spoils__xp-glyph" aria-hidden="true">★</span><span className="fk-spoils__item-name">+{m.xpEach} XP</span></li>
                  </ul>
                  <div className="fk-spoils__holder">
                    {holder !== undefined
                      ? <><HeroFace hero={h} beast={beast(holder)} /><span className="fk-spoils__hero-name">{h?.name ?? `hero ${holder}`}{beast(holder) ? <small> &amp; {beast(holder)!.name}</small> : null}</span>{holderMine && !given && <Badge tone="gold">yours</Badge>}</>
                      : <span className="fk-spoils__free">{given ? 'unclaimed' : act ? 'Click to claim' : 'free'}</span>}
                    {given && holder !== undefined && <Badge tone="green">given</Badge>}
                    {given && p.soldGold > 0 && <Badge tone="amber" title="The bag was full: the overflow was sold">+{p.soldGold} gold from a full bag</Badge>}
                  </div>
                  {!given && pileItems.length > 0 && <span className="fk-spoils__from">from {[...new Set(pileItems.map((i) => STASH_NAMES[i.stash] ?? 'a stash'))].join(', ')}</span>}
                </div>
              );
            })}
          </div>
          {/* docs/84 SP4: what a piece does, by clicking it in a pile */}
          <aside className="fk-spoils__detail" aria-label="Item details" data-testid="spoils-detail">
            {shown ? (
              <>
                <div className="fk-spoils__detail-head">
                  <ItemIcon def={shown.def} tier={shown.tier} name={shown.name} relic={shown.relic} scale={3} framed />
                  <div>
                    <b className="fk-spoils__detail-name">{shown.name}</b>
                    <span className="fk-spoils__detail-grade">{gradeWord(shown.def, shown.tier, shown.relic)}{shown.pile !== undefined && shown.pile !== null ? ` · Pile ${shown.pile + 1}` : null}</span>
                  </div>
                </div>
                {shownDef ? <ItemBody item={shownDef} compact /> : <GearBody def={shown.def} tier={shown.tier} relic={shown.relic} />}
              </>
            ) : (
              <p className="fk-spoils__detail-hint">Click an item in a pile to see what it does.</p>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}

/**
 * The bottom bar while the Spoils are up (in place of the hotbar): who you are choosing for (a
 * ranger with their beast), the timer, who has locked in, Lock in / Unlock, "View the field" and
 * Continue. It carries `spoils-screen` (always on screen, folded or not).
 */
export function SpoilsBar({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const m = useSpoilsModel();
  const { encounterId, division, mine, owners, lockedBy, iLocked, given, heldBy, choosing, setForHero, heroById, callings, names, me } = m;
  const markDone = useSpoils((s) => s.markDone);
  const [now, setNow] = useState(serverNow());
  useEffect(() => { const id = setInterval(() => setNow(serverNow()), 250); return () => clearInterval(id); }, []);
  const deadline = tsToMs(division?.deadlineAt);
  const left = deadline !== undefined ? secondsUntil(deadline, now) : 0;
  const canLeave = !division || given || iLocked || mine.length === 0;
  const choosingNow = !!division && division.piles > 0 && !given;
  const waiting = owners.filter((o) => !lockedBy.has(o)).map((o) => (o === me ? 'you' : names[o] ?? o.slice(-6)));
  const onContinue = () => { if (encounterId !== undefined) markDone(encounterId); };

  return (
    <div className="fk-spoils-bar" data-testid="spoils-screen" data-state={given ? 'given' : division ? 'choosing' : 'waiting'}>
      {choosingNow && mine.length > 1 && choosing !== undefined && (
        <Segmented className="fk-spoils-bar__for" label="Choosing for" value={choosing.toString()} onChange={(v) => setForHero(v)} disabled={iLocked}
          options={mine.map((h) => {
            const hero = heroById.get(h.toString());
            const b = beastOf(callings, h);
            return { value: h.toString(), title: heldBy(h) ? `${hero?.name} holds pile ${heldBy(h)!.index + 1}` : `${hero?.name} has no pile yet`, label: <><HeroFace hero={hero} beast={b} />{hero?.name ?? `hero ${h}`}{heldBy(h) ? <span className="fk-spoils-bar__held" aria-hidden="true">pile {heldBy(h)!.index + 1}</span> : null}</> };
          })} />
      )}
      {choosingNow && (
        <div className="fk-spoils-bar__clock">
          <Meter value={left} max={CHOOSE_S} kind={left <= 20 ? 'hp' : 'gold'} label="Choosing" format={() => formatMmSs(left)} data-testid="spoils-timer" />
          <span className="fk-spoils-bar__locks" data-testid="spoils-locks" title={waiting.length ? `Still choosing: ${waiting.join(', ')}. When the timer runs out, heroes without a pile take what is left.` : undefined}>
            {lockedBy.size} of {owners.length} locked in{owners.length > 1 && waiting.length ? ` · waiting on ${waiting.join(', ')}` : ''}
          </span>
        </div>
      )}
      {!choosingNow && <span className="fk-spoils-bar__done">{given ? (m.explore ? 'Finders keepers: each hero keeps what they found.' : 'The spoils are given.') : division ? 'Nothing to divide.' : 'Counting the haul…'}</span>}
      <span className="fk-spacer" />
      <Button onClick={onToggle} data-testid="spoils-toggle" aria-pressed={!open} title={open ? 'Fold the spoils away and look over the field' : 'Bring the spoils back'}>
        {open ? 'View the field' : 'Show the spoils'}
      </Button>
      {choosingNow && mine.length > 0 && (iLocked
        ? <Button size="lg" onClick={() => void spoilsRpc.unlockRewards(division.encounterId)} data-testid="spoils-unlock">Unlock to switch</Button>
        : <Button primary size="lg" onClick={() => void spoilsRpc.lockRewards(division.encounterId)} disabled={mine.some((h) => !heldBy(h))} data-sfx="ui.coins" data-testid="spoils-lock" title={mine.some((h) => !heldBy(h)) ? 'Every hero of yours needs a pile first' : undefined}>Lock in</Button>)}
      <Reveal className="fk-spoils-bar__continue">
        <Button primary={canLeave} size="lg" onClick={onContinue} disabled={!canLeave} data-testid="spoils-continue" title={canLeave ? undefined : 'Lock in your pile first (or wait for the timer)'}>
          Continue
        </Button>
      </Reveal>
    </div>
  );
}
